// filepath: motor-risk.component.ts
import { Component, OnInit } from '@angular/core';
import { MotorRisk, MotorRiskRequest, MotorRiskUpdateRequest, MotorRiskService } from './motor-risk.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-motor-risk',
  templateUrl: './motor-risk.component.html',
  styleUrls: ['./motor-risk.component.scss']
})
export class MotorRiskComponent implements OnInit {

  risks: MotorRisk[] = [];
  filteredRisks: MotorRisk[] = [];

  isLoading = false;
  viewMode: 'list' | 'form' = 'list';
  isEditing = false;
  selectedRisk: MotorRisk | null = null;

  // Filter
  enabledFilter: string = ''; // '' = all, 'true' = enabled, 'false' = disabled

  // Search
  searchQuery = '';

  // Pagination
  currentPage = 0;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  // Discount flag
  discountEnabled = false;
  discountLoading = false;

  // Form
  riskForm: MotorRiskRequest = this.getEmptyForm();
  formErrors: { [key: string]: string } = {};
  formTouched: { [key: string]: boolean } = {};

  constructor(private riskService: MotorRiskService) {}

  get displayedEnd(): number {
    return Math.min((this.currentPage + 1) * this.pageSize, this.filteredRisks.length);
  }

  ngOnInit() {
    this.loadRisks();
    this.loadDiscountFlag();
  }

  // ── Discount flag ────────────────────────────────────────
  loadDiscountFlag() {
    this.discountLoading = true;
    this.riskService.getDiscountFlag().subscribe(
      (response) => {
        this.discountLoading = false;
        this.discountEnabled = response.allowDiscount === true;
      },
      (error) => {
        this.discountLoading = false;
        const msg = (error && error.error && (error.error.responseMessage || error.error.message)) || 'Unable to load discount setting.';
        Swal.fire({ title: 'Error', text: msg, icon: 'error' });
      }
    );
  }

  toggleDiscount(event: Event) {
    // Keep the checkbox bound to server state until the update succeeds.
    event.preventDefault();
    if (this.discountLoading) return;

    const newState = !this.discountEnabled;
    const label = newState ? 'enable' : 'disable';

    Swal.fire({
      title: `${newState ? 'Enable' : 'Disable'} Discounts?`,
      text: `Are you sure you want to ${label} discounts on new policies?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#6B2D8B',
      cancelButtonColor: '#a0a0b0',
      confirmButtonText: `Yes, ${label}`,
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (!result.value) return;
      this.discountLoading = true;
      this.riskService.setDiscountFlag(newState).subscribe(
        (response) => {
          this.discountLoading = false;
          this.discountEnabled = response && typeof response.allowDiscount === 'boolean' ? response.allowDiscount : newState;
          Swal.fire({
            title: 'Updated!',
            text: `Discounts on new policies have been ${this.discountEnabled ? 'enabled' : 'disabled'}.`,
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        },
        (error) => {
          this.discountLoading = false;
          const msg = (error && error.error && (error.error.responseMessage || error.error.message)) || 'Unable to update discount setting. Please try again.';
          Swal.fire({ title: 'Error', text: msg, icon: 'error' });
        }
      );
    });
  }

  getEmptyForm(): MotorRiskRequest {
    return { tabledCode: null, tabledDesignation: '', enabled: true };
  }

  // ── Load ─────────────────────────────────────────────────
  loadRisks() {
    this.isLoading = true;
    const filter = this.enabledFilter === '' ? null : this.enabledFilter === 'true';

    this.riskService.getAllRisks(filter).subscribe(
      (response) => {
        this.isLoading = false;
        if (response.responseCode === '000') {
          this.risks = response.data.risks || [];
          this.totalItems = response.data.totalItems;
          this.totalPages = response.data.totalPages;
          this.currentPage = response.data.currentPage;
          this.applySearch();
        } else {
          Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to load risks.', icon: 'error' });
        }
      },
      (error) => {
        this.isLoading = false;
        console.error('Error loading risks:', error);
        Swal.fire({ title: 'Error', text: (error && error.error && error.error.responseMessage) || 'Unable to connect to the server.', icon: 'error' });
      }
    );
  }

  // ── Search & Filter ──────────────────────────────────────
  applySearch() {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredRisks = q
      ? this.risks.filter(r => r.tabledDesignation.toLowerCase().includes(q) || r.tabledCode.toString().includes(q))
      : this.risks.slice();
  }

  onSearch() {
    this.applySearch();
  }

  onFilterChange() {
    this.currentPage = 0;
    this.loadRisks();
  }

  // ── Toggle enabled/disabled ──────────────────────────────
  toggleEnabled(risk: MotorRisk) {
    const newState = !risk.enabled;
    const label = newState ? 'enable' : 'disable';

    Swal.fire({
      title: `${newState ? 'Enable' : 'Disable'} Risk?`,
      text: `Are you sure you want to ${label} "${risk.tabledDesignation}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#6B2D8B',
      cancelButtonColor: '#a0a0b0',
      confirmButtonText: `Yes, ${label}`,
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.value) {
        this.isLoading = true;
        this.riskService.toggleEnabled(risk.id, newState).subscribe(
          (response) => {
            this.isLoading = false;
            if (response.responseCode === '000') {
              Swal.fire({
                title: 'Updated!',
                text: `"${risk.tabledDesignation}" has been ${newState ? 'enabled' : 'disabled'}.`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
              });
              this.loadRisks();
            } else {
              Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to update.', icon: 'error' });
            }
          },
          (error) => {
            this.isLoading = false;
            const msg = (error && error.error && error.error.responseMessage) || 'Unable to update risk status. Please try again.';
            Swal.fire({ title: 'Error', text: msg, icon: 'error' });
          }
        );
      }
    });
  }

  // ── Open form for creating ────────────────────────────────
  openCreateForm() {
    this.riskForm = this.getEmptyForm();
    this.formErrors = {};
    this.formTouched = {};
    this.isEditing = false;
    this.selectedRisk = null;
    this.viewMode = 'form';
  }

  // ── Open form for editing ─────────────────────────────────
  openEditForm(risk: MotorRisk) {
    this.riskForm = {
      tabledCode: risk.tabledCode,
      tabledDesignation: risk.tabledDesignation,
      enabled: risk.enabled
    };
    this.selectedRisk = risk;
    this.formErrors = {};
    this.formTouched = {};
    this.isEditing = true;
    this.viewMode = 'form';
  }

  backToList() {
    this.viewMode = 'list';
    this.selectedRisk = null;
  }

  // ── Validation ────────────────────────────────────────────
  markTouched(field: string) {
    this.formTouched[field] = true;
    this.validateField(field);
  }

  validateField(field: string) {
    const val = this.riskForm[field];
    switch (field) {
      case 'tabledCode':
        if (val === null || val === undefined || val === '') {
          this.formErrors[field] = 'Risk code is required.';
        } else if (isNaN(Number(val)) || Number(val) <= 0) {
          this.formErrors[field] = 'Risk code must be a positive number.';
        } else {
          delete this.formErrors[field];
        }
        break;
      case 'tabledDesignation':
        if (!val || !String(val).trim()) {
          this.formErrors[field] = 'Risk name/designation is required.';
        } else if (String(val).trim().length < 2) {
          this.formErrors[field] = 'Designation must be at least 2 characters.';
        } else {
          delete this.formErrors[field];
        }
        break;
    }
  }

  validateAll(): boolean {
    ['tabledCode', 'tabledDesignation'].forEach(f => {
      this.formTouched[f] = true;
      this.validateField(f);
    });
    return Object.keys(this.formErrors).length === 0;
  }

  hasError(field: string): boolean {
    return this.formTouched[field] && !!this.formErrors[field];
  }

  // ── Save ─────────────────────────────────────────────────
  saveRisk() {
    if (!this.validateAll()) return;
    this.isLoading = true;

    const payload = { ...this.riskForm, tabledCode: Number(this.riskForm.tabledCode) };

    if (this.isEditing) {
      const updatePayload = {
        tabledCode:        Number(this.riskForm.tabledCode),
        tabledDesignation: this.riskForm.tabledDesignation
      };
      this.riskService.updateRisk(this.selectedRisk.id, updatePayload).subscribe(
        (response) => {
          this.isLoading = false;
          if (response.responseCode === '000') {
            Swal.fire({ title: 'Updated!', text: `"${payload.tabledDesignation}" has been updated.`, icon: 'success', timer: 2000, showConfirmButton: false });
            this.viewMode = 'list';
            this.loadRisks();
          } else {
            Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to update.', icon: 'error' });
          }
        },
        (error) => {
          this.isLoading = false;
          const msg = (error && error.error && error.error.responseMessage) || 'Unable to update risk. Please try again.';
          Swal.fire({ title: 'Error', text: msg, icon: 'error' });
        }
      );
    } else {
      this.riskService.createRisk(payload).subscribe(
        (response) => {
          this.isLoading = false;
          if (response.responseCode === '000') {
            Swal.fire({ title: 'Created!', text: `"${payload.tabledDesignation}" has been added.`, icon: 'success', timer: 2000, showConfirmButton: false });
            this.viewMode = 'list';
            this.loadRisks();
          } else {
            Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to create risk.', icon: 'error' });
          }
        },
        (error) => {
          this.isLoading = false;
          const msg = (error && error.error && error.error.responseMessage) || 'Unable to create risk. Please try again.';
          Swal.fire({ title: 'Error', text: msg, icon: 'error' });
        }
      );
    }
  }

  // ── Delete ────────────────────────────────────────────────
  deleteRisk(risk: MotorRisk) {
    Swal.fire({
      title: 'Delete Risk?',
      text: `Are you sure you want to delete "${risk.tabledDesignation}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#6B2D8B',
      cancelButtonColor: '#a0a0b0',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.value) {
        this.isLoading = true;
        this.riskService.deleteRisk(risk.id).subscribe(
          (response) => {
            this.isLoading = false;
            if (response.responseCode === '000') {
              Swal.fire({ title: 'Deleted!', text: `"${risk.tabledDesignation}" has been removed.`, icon: 'success', timer: 2000, showConfirmButton: false });
              this.loadRisks();
            } else {
              Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to delete.', icon: 'error' });
            }
          },
          (error) => {
            this.isLoading = false;
            const msg = (error && error.error && error.error.responseMessage) || 'Unable to delete risk. Please try again.';
            Swal.fire({ title: 'Error', text: msg, icon: 'error' });
          }
        );
      }
    });
  }

  // ── Pagination ────────────────────────────────────────────
  get pagedRisks(): MotorRisk[] {
    const start = this.currentPage * this.pageSize;
    return this.filteredRisks.slice(start, start + this.pageSize);
  }

  get displayedTotalPages(): number {
    return Math.ceil(this.filteredRisks.length / this.pageSize);
  }

  goToPage(page: number) {
    if (page < 0 || page >= this.displayedTotalPages) return;
    this.currentPage = page;
  }

  nextPage() { this.goToPage(this.currentPage + 1); }
  prevPage() { this.goToPage(this.currentPage - 1); }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const max = 5;
    let start = Math.max(0, this.currentPage - Math.floor(max / 2));
    let end = Math.min(this.displayedTotalPages, start + max);
    if (end - start < max) start = Math.max(0, end - max);
    for (let i = start; i < end; i++) pages.push(i);
    return pages;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
}
